import { useLayoutEffect, useRef, useState } from 'react'
import type { Ticket, UserProfile } from '@/lib/types'
import { cn, formatDate, groupMedia, labelColor, PRIORITY_META } from '@/lib/utils'
import { sanitizeRichHtml } from '@/lib/richtext'
import { Avatar } from './Avatar'
import {
  Image as ImageIcon,
  Video as VideoIcon,
  Mic,
  Paperclip,
  FileText,
} from 'lucide-react'

interface Props {
  ticket: Ticket | null
  /** Bounding rect of the hovered card, used to position the popover. */
  anchor: DOMRect | null
  profilesById: Record<string, UserProfile>
}

/** Lightweight popover shown on card hover — full description + media summary,
 *  positioned beside the card (flips left if there's no room on the right). */
export function HoverPreview({ ticket, anchor, profilesById }: Props) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    if (!ticket || !anchor || !ref.current) {
      setPos(null)
      return
    }
    const el = ref.current
    const w = el.offsetWidth
    const h = el.offsetHeight
    const gap = 10
    // Prefer right of the card; flip left if it would overflow the viewport.
    let left = anchor.right + gap
    if (left + w > window.innerWidth - 8) left = anchor.left - gap - w
    if (left < 8) left = 8
    let top = anchor.top
    if (top + h > window.innerHeight - 8) top = window.innerHeight - 8 - h
    if (top < 8) top = 8
    setPos({ top, left })
  }, [ticket, anchor])

  if (!ticket || !anchor) return null

  const media = groupMedia(ticket.media_urls)

  return (
    <div
      ref={ref}
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        opacity: pos ? 1 : 0,
      }}
      className="pointer-events-none fixed z-50 w-72 rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-3.5 shadow-[var(--shadow-modal)] transition-opacity duration-100"
    >
      <div className="text-[13px] font-semibold leading-snug text-[var(--color-text-strong)]">
        {ticket.title}
      </div>
      {(ticket.priority || ticket.labels.length > 0) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {ticket.priority && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide ring-1 ring-inset',
                PRIORITY_META[ticket.priority].chip,
              )}
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  PRIORITY_META[ticket.priority].dot,
                )}
              />
              {PRIORITY_META[ticket.priority].label}
            </span>
          )}
          {ticket.labels.map((label) => (
            <span
              key={label}
              className={cn(
                'rounded-md px-1.5 py-0.5 text-[9.5px] font-medium ring-1 ring-inset',
                labelColor(label),
              )}
            >
              {label}
            </span>
          ))}
        </div>
      )}
      {ticket.description ? (
        <div
          className="rich-text mt-1.5 max-h-48 overflow-hidden text-[12px] leading-relaxed text-[var(--color-text)]"
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(ticket.description) }}
        />
      ) : (
        <div className="mt-1.5 text-[12px] italic text-[var(--color-text-dim)]">
          Pas de description.
        </div>
      )}

      <div className="mt-2.5 flex items-center gap-2 border-t border-[var(--color-border-soft)] pt-2 text-[10.5px] text-[var(--color-text-dim)]">
        {ticket.assignee_id && (
          <Avatar
            profile={profilesById[ticket.assignee_id]}
            userId={ticket.assignee_id}
            className="h-4 w-4"
            textClassName="text-[7px]"
          />
        )}
        <span>{formatDate(ticket.source_date ?? ticket.created_at)}</span>
        {ticket.source_message_ids.length > 0 && (
          <span>{ticket.source_message_ids.length} msg</span>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          {media.image.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[var(--color-link)]">
              <ImageIcon className="h-3 w-3" />
              {media.image.length}
            </span>
          )}
          {media.video.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[var(--color-link)]">
              <VideoIcon className="h-3 w-3" />
              {media.video.length}
            </span>
          )}
          {media.audio.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[var(--color-link)]">
              <Mic className="h-3 w-3" />
              {media.audio.length}
            </span>
          )}
          {media.pdf.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[var(--color-link)]">
              <FileText className="h-3 w-3" />
              {media.pdf.length}
            </span>
          )}
          {media.file.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[var(--color-link)]">
              <Paperclip className="h-3 w-3" />
              {media.file.length}
            </span>
          )}
        </span>
      </div>
    </div>
  )
}
